<template>
  <div class="params-container" v-if="filters.length">
    <h2 class="form-section-header">{{ title }}</h2>
    <h3 v-for="{ label, value } in filters" :key="label" class="black-text">
      {{ label }}:
      <span class="black-text" style="font-weight: normal">
        {{ value }}
      </span>
    </h3>
  </div>
</template>

<script>
export default {
  name: "ScheduledReportParams",
  props: {
    params: { required: true },
    title: { required: true },
  },

  data() {
    return {
      filters: [],
    };
  },

  watch: {
    params: {
      deep: true,
      handler(val) {
        this.filters = [];
        if (val && Object.keys(val).length) {
          const { servicePath, query } = val;

          if (servicePath === "call-history") {
            const labelMap = {
              tenantId: "Tenant Id",
              direction: "Direction",
              extNumber: "Extension Number",
              branch: "Branch",
              phoneNumber: "Phone Number",
              callerName: "Caller Name",
              callId: "Call Id",
              callOutcome: "Call Outcome",
              dispositions: "Dispositions",
              presentedFilter: "Presented Filter",
              timezone: "Timezone",
            };

            for (const key in query) {
              if (!["queryType", "primaryFilter"].includes(key)) {
                // ⭐️ Object Type ⭐️ //
                if (query[key] && query[key].constructor === {}.constructor) {
                  if (query[key].value)
                    if (["extNumber", "branch", "phoneNumber"].includes(key)) {
                      this.filters.push({
                        label: `Filter by ${labelMap[key]}`,
                        value: query[key].label,
                      });
                    } else {
                      this.filters.push({
                        label: labelMap[key],
                        value: `${query[key].label}(${query[key].value})`,
                      });
                    }
                } else if (query[key]) {
                  if (["callerName", "callId", "callOutcome"].includes(key)) {
                    this.filters.push({
                      label: `Filter by ${labelMap[key]}`,
                      value: query[key],
                    });
                  } else {
                    this.filters.push({
                      label: labelMap[key],
                      value: query[key].toString().replace(/,/g, ", "),
                    });
                  }
                }
              }
            }
          } else if (
            (servicePath === "account-statistics" &&
              ["agentActivity", "queueSummary"].includes(query.type)) ||
            servicePath === "queue-log"
          ) {
            this.formatQueueFilters(query);
          } else if (
            servicePath === "account-statistics" &&
            ["users", "phoneNumbers"].includes(query.type)
          ) {
            this.formatCallStatsFilters(query);
          }
        }
      },
    },
  },

  methods: {
    formatQueueFilters(query) {
      const {
        tenantId,
        tenantCode,
        queueIds,
        queueId,
        agent,
        agents,
        tag,
        theCallId,
        event,
        distribution,
        timezone,
      } = query;

      // ⭐️ Format Tenant Info ⭐️ //
      if (tenantId) {
        this.filters.push({
          label: "Tenant Id",
          value: tenantCode
            ? `${tenantId.label}(${tenantId.value}) - ${tenantCode}`
            : `${tenantId.label}(${tenantId.value})`,
        });
      }

      // ⭐️ Format Filter By Queues ⭐️ //
      let queues = [];
      if (queueIds && queueIds.length) {
        queues = queueIds;
      }

      if (queueId && queueId.length) {
        queues = queueId;
      }

      if (queues.length) {
        this.filters.push({
          label: "Filter by Queues",
          value: queues.map((q) => q.label).join(", "),
        });
      }

      // ⭐️ Format Filter By Users ⭐️ //
      let users = [];
      if (agents && agents.length) {
        users = agents;
      }

      if (agent && agent.length) {
        users = agent;
      }

      if (users.length) {
        this.filters.push({
          label: "Filter by Users",
          value: users.map((name) => name.split("_").join(" ")).join(", "),
        });
      }

      // ⭐️ Format Events ⭐️ //
      if (event && event.length) {
        const eventMap = {
          COMPLETECALLER: "Complete Caller",
          COMPLETEAGENT: "Complete Agent",
          LOGIN: "Login",
          LOGOUT: "Logout",
          ABANDON: "Abandon",
          RINGNOANSWER: "Refused",
          EXITWITHKEY: "Exit Key",
          EXITWITHTIMEOUT: "Exit Timeout",
          PAUSE: "Pause",
          UNPAUSE: "Unpause",
          BLINDTRANSFER: "Blind Transfer",
          ATTENDEDTRANSFER: "Attended Transfer",
          "CALLBACK ATTEMPT": "Callback Attempt",
          "CALLBACK TIMEOUT": "Callback Timeout",
        };
        this.filters.push({
          label: "Events",
          value: event.map((ev) => eventMap[ev]).join(", "),
        });
      }

      // ⭐️ Format Tag ⭐️ //
      if (tag) {
        this.filters.push({
          label: "Call Tag",
          value: tag,
        });
      }

      // ⭐️ Format the Call ID ⭐️ //
      if (theCallId) {
        this.filters.push({
          label: "Call ID",
          value: theCallId,
        });
      }

      // ⭐️ Format distribution ⭐️ //
      if (distribution) {
        const distributionMap = {
          hourly: "Hourly",
          daily: "Daily",
          monthly: "Monthly",
          agents: "Agents",
          none: "None",
        };

        this.filters.push({
          label: "Distribution",
          value: distributionMap[distribution],
        });
      }

      // ⭐️ Format Timezone ⭐️ //
      if (timezone) {
        this.filters.push({
          label: "Timezone",
          value: timezone,
        });
      }
    },

    formatCallStatsFilters(query) {
      const { tenantId, timezone, extNums, numbers, branch, distribution } =
        query;

      if (tenantId) {
        this.filters.push({
          label: "Tenant Id",
          value: `${tenantId.label}(${tenantId.value})`,
        });
      }

      if (branch) {
        this.filters.push({
          label: "Filter by Branch",
          value: branch.label,
        });
      }

      if (extNums && extNums.length) {
        this.filters.push({
          label: "Filter by Users",
          value: extNums.map((ext) => ext.label).join(", "),
        });
      }

      if (numbers && numbers.length) {
        this.filters.push({
          label: "Filter by Numbers",
          value: numbers.map((ext) => ext.label).join(", "),
        });
      }

      if (distribution) {
        const distributionMap = {
          daily: "Daily",
          monthly: "Monthly",
          none: "None",
        };

        this.filters.push({
          label: "Distribution",
          value: distributionMap[distribution],
        });
      }

      if (timezone) {
        this.filters.push({
          label: "Timezone",
          value: timezone,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.params-container {
  max-width: 600px;
  margin: 0 auto;
}
</style>
